import { FocusableOption, FocusKeyManager } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CartItemFacade } from '@features/cart-item/facades/cart-item-facade/cart-item.facade';
import { PointsAccountFacade } from '@features/points-account/facades/points-account/points-account.facade';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { UserPreferenceFacade } from '@features/user-preference/facades/user-preference/user-preference.facade';
import { TranslateService } from '@ngx-translate/core';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { LoyaltyCurrencyFacade } from '@rd/core/facades/loyalty-currency-facade/loyalty-currency.facade';
import { GuardhouseService } from '@rd/core/services/guardhouse/guardhouse.service';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { PointsNumberService } from '@rd/core/services/points-number/points-number.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { BRAND_NAME } from '@rd/core/tokens/rd-lib.token';
import { PointsSummaryHelper } from '@rd/helpers/points-summary.helper';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import {
  FeatureFlags,
  GuardHouseLogoutCodes,
  LoyaltyCurrency,
  NavLink,
  PointsAccount,
  PointsSummary,
  UserPreference,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { isExternalUrl } from '@rd/utils/url.utils';
import { AnzTooltipsComponent } from '@rd/wl/anz/components/tooltips/tooltips.component';
import { ANZFeatureFlagService } from '@rd/wl/anz/services/anz-feature-flag.service';
import { AnzPointsExpiryService } from '@rd/wl/anz/services/expiry-date/points-expiry.service';
import { combineLatest, Observable, Subject, timer } from 'rxjs';
import {
  debounce,
  distinctUntilChanged,
  filter,
  map,
  switchMapTo,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AnzAuthSecureRouteDialogComponent } from '../../components/auth-secure-route-dialog/auth-secure-route-dialog.component';
import { AnzCardSelectorComponent } from '../../components/card-selector/card-selector.component';
import { AnzNavLinksService } from '../../services/anz-nav-links.service';
import { ANZ_SSO_ENABLED } from '../../tokens/features.tokens';
import { PageUrlSegment } from '../../types';
import { getMainUrlSegment } from '../../utils/anz-page-discovery.utils';

type MenuSection = 'redeem' | 'myAccount' | 'home';
type UserAction = 'login' | 'logout' | 'enroll';

@Component({
  selector: 'rd-anz-desktop-header',
  templateUrl: './desktop-header.component.html',
  styleUrls: ['./desktop-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnzDesktopHeaderComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  readonly shoppingCartFeatureFlag = FeatureFlags.ShoppingCart;

  isAnzSSOEnabled = inject(ANZ_SSO_ENABLED);

  constructor(
    public router: Router,
    public cdRef: ChangeDetectorRef,
    public urlUtilsService: UrlUtilsService,
    public navlinksService: AnzNavLinksService,
    public guardhouseService: GuardhouseService,
    public pointsSummaryFacade: PointsSummaryFacade,
    private dialogService: DialogService,
    private pointsSummaryHelper: PointsSummaryHelper,
    private pointsNumberService: PointsNumberService,
    private userPreferenceFacade: UserPreferenceFacade,
    private iconRegistryService: IconRegistryService,
    private pointsExpiryService: AnzPointsExpiryService,
    public pointsAccountFacade: PointsAccountFacade,
    public cartItemFacade: CartItemFacade,
    public loyaltyCurrencyFacade: LoyaltyCurrencyFacade,
    public authFacade: AuthFacade,
    public featureFlagService: ANZFeatureFlagService,
    public translateService: TranslateService,
    @Inject(BRAND_NAME) public brandName: 'ANZ-BIZ' | 'ANZ',
  ) {
    this.expiryPoints$.subscribe((expiryPoints: string | undefined) => {
      if (!!expiryPoints) {
        this.showExpiryPointsTooltip();
      } else {
        this.closeExpiryPointsTooltip();
      }
    });
  }

  get activeElement(): Element | null {
    return document.activeElement;
  }

  get logoImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/header-logo.png');
  }

  pointsAccountId$: Observable<string | undefined> =
    this.userPreferenceFacade.userPreferences$.pipe(
      map((userPreference: UserPreference) => userPreference.pointsAccountId),
    );

  @HostBinding('class.rd-anz-desktop-header') componentClass = true;

  @ViewChildren('accountLink')
  accountItems: QueryList<HTMLAnchorElement>;

  @ViewChildren('redeemLink')
  redeemItems: QueryList<HTMLAnchorElement>;

  @ViewChildren(AnzCardSelectorComponent)
  items: QueryList<AnzCardSelectorComponent>;

  @ViewChild('button') button: ElementRef;

  @ViewChild('expiryPointsTooltips')
  expiryPointsTooltipsRef: AnzTooltipsComponent;

  @ViewChild('expiryPointsButton') expiryPointsButton: ElementRef;

  selectedMenuItem: MenuSection;
  selectedChildMenuItem?: NavLink;
  isLoggedIn$: Observable<boolean> = this.authFacade.isLoggedIn$;
  // pages which has banner like: listing, homepage
  // need to show the header over the banner image e.g. position: absolute
  shouldUseAbsolutePosition: boolean;
  loyaltyCurrency$: Observable<LoyaltyCurrency> =
    this.loyaltyCurrencyFacade.getLoyaltyCurrencySelected$.pipe(
      filter(isNotNull),
    );
  destroy$ = new Subject<void>();
  redeemLinks: string[] = [];
  myAccountLinks: string[] = [];
  homeAndStaticLinks: string[] = [];
  open = false;
  currentUrlSegment: PageUrlSegment;
  logoAltText = '';
  cardKeyManager: FocusKeyManager<AnzCardSelectorComponent>;
  redeemCardManager: FocusKeyManager<HTMLAnchorElement>;
  accountManager: FocusKeyManager<HTMLAnchorElement>;

  activePointsAccount$: Observable<PointsAccount | undefined> = combineLatest([
    this.pointsAccountFacade.pointsAccounts$,
    this.pointsAccountId$,
  ]).pipe(
    map(([pointsAccounts, id]) =>
      pointsAccounts.find((account) => account.id === id),
    ),
  );

  pointsSummary$: Observable<PointsSummary | null> =
    this.pointsSummaryFacade.pointsSummary$.pipe(
      filter(isNotNull),
      map((pointsSummary: PointsSummary) => pointsSummary),
    );

  expiryPoints$: Observable<string | undefined> = this.isLoggedIn$.pipe(
    filter((isLoggedIn: boolean) => !!isLoggedIn),
    switchMapTo(
      this.pointsSummary$.pipe(filter(isNotNull), distinctUntilChanged()),
    ),
    map((activePointsSummary: PointsSummary) => {
      const expiryPoints = this.pointsNumberService.roundNumber(
        this.pointsSummaryHelper.expiringPointsAtDate(
          activePointsSummary,
          this.pointsExpiryService.lastDayInExpiryYear(),
        ),
      );

      return expiryPoints !== '0' ? expiryPoints : '';
    }),
  );

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const redeemButtonGroup = Array.from(
      document.querySelectorAll('.redeem-group'),
    );
    const accountButtonGroup = Array.from(
      document.querySelectorAll('.account-group'),
    );

    if (this.open) {
      return this.cardKeyManager?.onKeydown(event);
    }

    if (!this.activeElement) {
      return;
    }

    if (
      this.selectedMenuItem === 'redeem' &&
      redeemButtonGroup.includes(this.activeElement)
    ) {
      return this.redeemCardManager?.onKeydown(event);
    }
    if (
      this.selectedMenuItem === 'myAccount' &&
      accountButtonGroup.includes(this.activeElement)
    ) {
      return this.accountManager?.onKeydown(event);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(): void {
    const redeemButtonGroup = Array.from(
      document.querySelectorAll('.redeem-group'),
    );
    const myAccountGroup = Array.from(
      document.querySelectorAll('.account-group'),
    );

    if (this.open) {
      this.closeCardSelector();
      return;
    }

    if (!this.activeElement) {
      return;
    }

    switch (this.selectedMenuItem) {
      case 'redeem': {
        if (redeemButtonGroup.includes(this.activeElement)) {
          this.redeemCardManager.setActiveItem(-1);
          document.getElementById('redeem')?.focus();
        }
        break;
      }
      case 'myAccount': {
        if (myAccountGroup.includes(this.activeElement)) {
          this.accountManager.setActiveItem(-1);
          document.getElementById('account')?.focus();
        }
        break;
      }
    }
  }

  ngOnInit(): void {
    this.listenUrlChanged();
    this.setupChildMenuLinks();
    this.setupShoppingCart();
    this.iconRegistryService.registerIcon('chevron-down');
    this.logoAltText =
      this.brandName === 'ANZ'
        ? 'ANZ Rewards logo'
        : 'ANZ Business Rewards logo';
  }

  ngAfterViewInit(): void {
    this.pointsAccountFacade.pointsAccounts$
      .pipe(
        takeUntil(this.destroy$),
        filter((pointsAccounts: PointsAccount[]) => pointsAccounts.length > 1),
        tap(() => {
          // only need to do this if users have more than one card
          this.cardKeyManager = new FocusKeyManager<AnzCardSelectorComponent>(
            this.items,
          );
          // Can't think of a more elegant way to do this for the meanwhile
          this.cardKeyManager.tabOut
            .pipe(
              takeUntil(this.destroy$),
              tap(() => this.closeCardSelector()),
            )
            .subscribe();
        }),
      )
      .subscribe();

    // We have to implement the focus method here to fulfill the interface
    this.redeemItems.forEach(
      (el, id) =>
        (el.focus = () => document.getElementById(`redeem-${id}`)?.focus()),
    );

    this.redeemCardManager = new FocusKeyManager<HTMLAnchorElement>(
      this.redeemItems as QueryList<FocusableOption & HTMLAnchorElement>,
    );

    this.redeemCardManager.tabOut
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.redeemCardManager.setActiveItem(-1)),
      )
      .subscribe();

    this.accountItems.forEach(
      (el, id) =>
        (el.focus = () => document.getElementById(`account-${id}`)?.focus()),
    );

    this.accountManager = new FocusKeyManager<HTMLAnchorElement>(
      this.accountItems as QueryList<FocusableOption & HTMLAnchorElement>,
    );

    this.accountManager.tabOut
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.accountManager.setActiveItem(-1)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupShoppingCart(): void {
    this.isLoggedIn$
      .pipe(
        take(1),
        tap((isLoggedIn) => {
          if (
            isLoggedIn &&
            this.featureFlagService.isEnable(FeatureFlags.ShoppingCart)
          ) {
            this.cartItemFacade.loadCart();
          }
        }),
      )
      .subscribe();
  }

  setupChildMenuLinks(): void {
    this.redeemLinks = this.navlinksService.redeem.map((item) => {
      return item.route;
    });
    this.myAccountLinks = this.navlinksService.rewards.map((item) => {
      return item.route;
    });

    const staticPagesLinks = this.navlinksService.staticPages.map((item) => {
      return item.route;
    });

    this.homeAndStaticLinks = ['', '/'].concat(staticPagesLinks);
  }

  listenUrlChanged(): void {
    // This is for initial page render
    // Redirect from GH login back to giftcards page
    this.currentUrlSegment = getMainUrlSegment(this.router.url);
    this.shouldUseAbsolutePosition = this.canUseAbsolutePosition();
    this.selectedMenuItem = this.initializeCurrentOpenMenu();
    this.cdRef.detectChanges();
    const urlChangedEvent$ = this.router.events
      .pipe(takeUntil(this.destroy$))
      .pipe(filter((event) => event instanceof NavigationEnd));

    urlChangedEvent$
      .pipe(
        filter(
          () =>
            !this.router.getCurrentNavigation()?.extras?.state
              ?.keepScrollPosition,
        ),
        debounce(() => {
          // navigationEnd event happens when the old route still being displayed
          // add a delay to avoid live announcer say the old route h1 content
          return timer(500);
        }),
      )
      .subscribe(() => {
        const heading = document.querySelector('h1');
        if (!heading) {
          return;
        }
        heading.classList.add('focus-link');
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      });

    urlChangedEvent$.subscribe((event) => {
      this.currentUrlSegment = getMainUrlSegment(
        (event as NavigationEnd).urlAfterRedirects,
      );
      this.selectedMenuItem = this.initializeCurrentOpenMenu();
      this.shouldUseAbsolutePosition = this.canUseAbsolutePosition();
      this.cdRef.detectChanges();
    });
  }

  onMenuItemClick(menuName: MenuSection): void {
    this.selectedMenuItem = menuName;
    if (menuName === 'redeem') {
      this.redeemCardManager.setActiveItem(-1);
    }
    if (menuName === 'myAccount') {
      this.accountManager.setActiveItem(-1);
    }
  }

  onChildMenuItemClick(childMenu: NavLink): void {
    this.selectedChildMenuItem = childMenu;
  }

  getExpandedStatus(menuName: MenuSection): boolean {
    return this.selectedMenuItem === menuName;
  }

  isActivatedChildMenuItem(urlSegment: string): boolean {
    const giftCardsPageList = [
      PageUrlSegment.GIFTCARD_LIST,
      PageUrlSegment.GIFTCARD_DETAILS,
      PageUrlSegment.GIFTCARD_REVIEW,
      PageUrlSegment.GIFTCARD_SUCCESS,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION_SUCCESS,
    ];

    const transferProgramsPageList = [
      PageUrlSegment.TRANSFER_PROGRAMS,
      PageUrlSegment.TRANSFER_LANDING,
      PageUrlSegment.TRANSFER_REVIEW,
      PageUrlSegment.TRANSFER_SUCCESS,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION_SUCCESS,
    ];

    const redeemCashPageList = [
      PageUrlSegment.REDEEMCASH_DETAILS,
      PageUrlSegment.REDEEMCASH_REVIEW,
      PageUrlSegment.REDEEMCASH_SUCCESS,
      PageUrlSegment.REDEEMCASH_ENROLLMENT,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION_SUCCESS,
    ];

    const orderPageList = [
      PageUrlSegment.ORDERS,
      PageUrlSegment.PAST_ORDERS,
      PageUrlSegment.MANAGE_REDEMPTIONS,
    ];

    const merchandisePageList = [
      PageUrlSegment.MERCHANDISE,
      PageUrlSegment.MERCHANDISE_ALL,
      PageUrlSegment.MERCHANDISE_TOP_50,
      PageUrlSegment.MERCHANDISE_CATEGORY,
      PageUrlSegment.MERCHANDISE_DETAILS,
    ];

    const everydaySpendPageList = [
      PageUrlSegment.EVERYDAY_SPEND_CHECKOUT,
      PageUrlSegment.EVERYDAY_SPEND_REVIEW,
      PageUrlSegment.EVERYDAY_SPEND_SUCCESS,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION_SUCCESS,
    ];

    switch (urlSegment) {
      case PageUrlSegment.GIFTCARD_LIST: {
        return giftCardsPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.TRANSFER_PROGRAMS: {
        return transferProgramsPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.REDEEMCASH_DETAILS: {
        return redeemCashPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.POINTS_TRANSACTIONS: {
        return this.currentUrlSegment === PageUrlSegment.POINTS_TRANSACTIONS;
      }
      case PageUrlSegment.MERCHANDISE: {
        return merchandisePageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.ORDERS: {
        return this.currentUrlSegment === PageUrlSegment.ORDERS;
      }
      case PageUrlSegment.MANAGE_REDEMPTIONS:
      case PageUrlSegment.PAST_ORDERS: {
        if (this.featureFlagService.isEnable(FeatureFlags.AutoRedemption)) {
          return orderPageList.includes(this.currentUrlSegment);
        }
        break;
      }
      case PageUrlSegment.EVERYDAY_SPEND_CHECKOUT: {
        return everydaySpendPageList.includes(this.currentUrlSegment);
      }
    }

    return false;
  }

  handleUserAction(action: UserAction): void {
    switch (action) {
      case 'enroll': {
        return this.guardhouseService.redirectToEnrollUrl();
      }
      case 'login': {
        if (this.isAnzSSOEnabled) {
          this.dialogService.open(AnzAuthSecureRouteDialogComponent, {
            id: 'auth-secure-route-dialog',
            screenMode: 'center-md',
            ariaLabel: this.translateService.instant(
              'accessibility.core.auth_dialog_aria_label',
            ),
            keepModalOnRouteChange: true,
          });
          return;
        }

        return this.guardhouseService.redirectToLoginUrl();
      }
      case 'logout': {
        this.guardhouseService.redirectToLogoutUrl(
          GuardHouseLogoutCodes.IntentionalLogout,
        );
      }
    }
  }

  isExternalNavLink(url: string): boolean {
    return isExternalUrl(url);
  }

  getButtonIconPath(): string {
    return this.urlUtilsService.imageUrl('/icons/padlock.svg');
  }

  isPointsAccountActive(
    pointsAccount: PointsAccount,
    pointsAccountId: string,
  ): boolean {
    return pointsAccount.id === pointsAccountId;
  }

  handleClick(pointsAccount: PointsAccount): void {
    this.userPreferenceFacade.userPreferences$
      .pipe(
        take(1),
        map((userPreference: UserPreference) => userPreference.pointsAccountId),
        tap((pointsAccountId: string | undefined) => {
          this.closeCardSelector();
          if (pointsAccountId !== pointsAccount.id) {
            this.userPreferenceFacade.updatePointsAccountId(pointsAccount.id);
          }
        }),
      )
      .subscribe();
  }

  handCardSelectorClick(): void {
    if (this.cardSelectorDisabled) {
      return;
    }
    this.open = !this.open;
    if (this.open) {
      this.cdRef.detectChanges();
      this.cardKeyManager.setActiveItem(-1);
    }
  }

  closeCardSelector(): void {
    this.open = false;
  }

  showExpiryPointsTooltip(): void {
    this.expiryPointsTooltipsRef?.open();
  }

  closeExpiryPointsTooltip(): void {
    this.expiryPointsTooltipsRef?.close();
  }

  onTooltipsVisibilityChanged(): void {
    this.expiryPointsButton.nativeElement.focus();
  }

  private canUseAbsolutePosition(): boolean {
    const containBannerRoutes = [
      PageUrlSegment.HOMEPAGE,
      PageUrlSegment.GIFTCARD_LIST,
      PageUrlSegment.TRANSFER_PROGRAMS,
      PageUrlSegment.REDEEMCASH_DETAILS,
      PageUrlSegment.MERCHANDISE,
      PageUrlSegment.MERCHANDISE_ALL,
      PageUrlSegment.MERCHANDISE_TOP_50,
      PageUrlSegment.MERCHANDISE_CATEGORY,
      PageUrlSegment.EVERYDAY_SPEND_CHECKOUT,
    ];
    return containBannerRoutes.includes(this.currentUrlSegment);
  }

  private initializeCurrentOpenMenu(): MenuSection {
    if (
      this.currentUrlSegment === PageUrlSegment.POINTS_TRANSACTIONS ||
      this.currentUrlSegment === PageUrlSegment.ORDERS ||
      (this.featureFlagService.isEnable(FeatureFlags.AutoRedemption) &&
        [
          PageUrlSegment.MANAGE_REDEMPTIONS,
          PageUrlSegment.PAST_ORDERS,
        ].includes(this.currentUrlSegment))
    ) {
      return 'myAccount';
    }

    return 'redeem';
  }

  get cardSelectorDisabled(): boolean {
    return [
      PageUrlSegment.GIFTCARD_REVIEW,
      PageUrlSegment.GIFTCARD_SUCCESS,
      PageUrlSegment.REDEEMCASH_REVIEW,
      PageUrlSegment.REDEEMCASH_SUCCESS,
      PageUrlSegment.TRANSFER_REVIEW,
      PageUrlSegment.TRANSFER_ADD_MEMBERSHIP,
      PageUrlSegment.TRANSFER_SUCCESS,
      PageUrlSegment.SHOPPING_CART_DELIVERY,
      PageUrlSegment.SHOPPING_CART_PAYMENT,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION_SUCCESS,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION_SUCCESS,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION_SUCCESS,
      PageUrlSegment.EVERYDAY_SPEND_REVIEW,
      PageUrlSegment.EVERYDAY_SPEND_SUCCESS,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION_SUCCESS,
    ].includes(this.currentUrlSegment);
  }

  get lastDayInYear(): string {
    return this.pointsExpiryService.lastDayInExpiryYearStr();
  }
}
